import { motion } from 'motion/react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import { appUrl } from '../lib/domains';
import { 
  CaseFileAnimation, 
  ProceedingsAnimation, 
  DiaryAnimation, 
  DraftAnimation, 
  FeeAnimation, 
  AnalyticsAnimation 
} from '../components/FeatureAnimations';

export default function HowItWorks() {
  const steps = [
    {
      title: "Create an AI-Powered Case File",
      description: "Lawyer selects case type, court, side represented, parties, and basic case information. Our AI analyzes your case to provide automatic extraction of details, helps lawyers understand all stages of the case, and references the accurate relevant law.",
      animation: <CaseFileAnimation />
    },
    {
      title: "Track Court Proceedings",
      description: "Record what happened on each hearing date and enter the next date with Fixed For.",
      animation: <ProceedingsAnimation />
    },
    {
      title: "Manage Diary",
      description: "Upcoming hearings automatically appear in the diary so the lawyer can see today, tomorrow, and future court dates.",
      animation: <DiaryAnimation />
    },
    {
      title: "Intelligent LexDraft",
      description: "Use LexDraft to create legal drafts connected with the case information. Our integrated AI drafting assistant helps you format, structure, and generate documents based on your case file context.",
      animation: <DraftAnimation />
    },
    {
      title: "Record Client Fee",
      description: "Enter total agreed fee and payments received over time.",
      animation: <FeeAnimation />
    },
    {
      title: "View Analytics",
      description: "See fee trends, workload, case types, and practice-level insights in the analytics screen.",
      animation: <AnalyticsAnimation />
    }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-slate-900 selection:text-white">
      <MarketingNav />

      <section className="pt-32 pb-20 px-6 max-w-4xl mx-auto text-center">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6"
        >
          How LexGat works
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto"
        >
          From case creation to daily practice management, LexGat keeps your litigation work structured and accessible.
        </motion.p>
      </section>

      <section className="py-20 px-6 max-w-4xl mx-auto">
        <h2 className="sr-only">Six steps from case setup to analytics</h2>
        <div className="relative border-l border-slate-200 ml-4 md:ml-0 md:border-l-0">
          {/* Vertical line for desktop */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 -translate-x-1/2"></div>
          
          <div className="space-y-24">
            {steps.map((step, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className={`relative flex flex-col md:flex-row items-center gap-12 md:gap-16 ${
                  idx % 2 === 0 ? 'md:flex-row-reverse' : ''
                }`}
              >
                {/* Mobile line connector placeholder for the dot */}
                <div className="absolute left-[-16.5px] md:left-1/2 top-4 md:top-1/2 w-8 h-8 md:-translate-x-1/2 md:-translate-y-1/2 flex items-center justify-center z-20">
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm border-4 border-white shadow-sm">
                    {idx + 1}
                  </div>
                </div>

                <div className="w-full md:w-1/2 text-left pl-8 md:pl-0 md:px-12 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                </div>
                
                <div className="w-full md:w-1/2 pl-8 pr-4 md:px-8 relative">
                   {step.animation}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-zinc-50 border-t border-zinc-200 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-4">
            Start managing your litigation practice with clarity.
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            Built for Pakistani lawyers. Designed around real court work.
          </p>
          <div className="flex justify-center items-center gap-4">
            <a
              href={appUrl('/login')}
              className="bg-slate-900 text-white px-8 py-3.5 rounded font-medium hover:bg-slate-800 transition-colors shadow-sm inline-block"
            >
              Get Started
            </a>
            <a
              href={appUrl('/login')}
              className="text-slate-700 bg-white border border-slate-200 px-8 py-3.5 rounded font-medium hover:bg-slate-50 transition-colors inline-block"
            >
              Login
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
